import React, { useContext, useEffect, useState, Suspense } from "react";
import { BrowserRouter, Redirect, Switch } from "react-router-dom";
import { ToastContainer } from "react-toastify";

import { ThemeProvider } from "../layout/themeContext";
import LoggedInLayout from "../layout";
import PageContentLoader from "../components/PageContentLoader";
import { AuthContext, AuthProvider } from "../context/Auth/AuthContext";
import { TicketsContextProvider } from "../context/Tickets/TicketsContext";
import { WhatsAppsProvider } from "../context/WhatsApp/WhatsAppsContext";
import Route from "./Route";
import { MANAGEMENT_PATHS } from "../utils/managementSections";
import { lazyWithReload } from "../utils/lazyWithReload";
import api from "../services/api";

import Signup from "../pages/Signup/";
import Login from "../pages/Login/";
import ForgetPassword from "../pages/ForgetPassWord/";
import TermsOfService from "../pages/Legal/TermsOfService";
import PrivacyPolicy from "../pages/Legal/PrivacyPolicy";
import DataDeletion from "../pages/Legal/DataDeletion";
import BackdropLoading from "../components/BackdropLoading";
import RouteTransitionFallback from "../components/RouteTransitionFallback";

const Dashboard = lazyWithReload(() => import("../pages/Dashboard/"));
const TicketResponsiveContainer = lazyWithReload(() => import("../pages/TicketResponsiveContainer"));
const Contacts = lazyWithReload(() => import("../pages/Contacts/"));
const Kanban = lazyWithReload(() => import("../pages/Kanban"));
const Chat = lazyWithReload(() => import("../pages/Chat"));
const Management = lazyWithReload(() => import("../pages/Management"));
const Connections = lazyWithReload(() => import("../pages/Connections/"));
const Users = lazyWithReload(() => import("../pages/Users"));
const Queues = lazyWithReload(() => import("../pages/Queues/"));
const SettingsCustom = lazyWithReload(() => import("../pages/SettingsCustom/"));
const Financeiro = lazyWithReload(() => import("../pages/Financeiro/"));
const Tags = lazyWithReload(() => import("../pages/Tags/"));
const MessagesAPI = lazyWithReload(() => import("../pages/MessagesAPI/"));
const Helps = lazyWithReload(() => import("../pages/Helps/"));
const ContactLists = lazyWithReload(() => import("../pages/ContactLists/"));
const ContactListItems = lazyWithReload(() => import("../pages/ContactListItems/"));
const QuickMessages = lazyWithReload(() => import("../pages/QuickMessages/"));
const Schedules = lazyWithReload(() => import("../pages/Schedules"));
const Campaigns = lazyWithReload(() => import("../pages/Campaigns"));
const CampaignsConfig = lazyWithReload(() => import("../pages/CampaignsConfig"));
const CampaignReport = lazyWithReload(() => import("../pages/CampaignReport"));
const Annoucements = lazyWithReload(() => import("../pages/Annoucements"));
const ToDoList = lazyWithReload(() => import("../pages/ToDoList/"));
const Subscription = lazyWithReload(() => import("../pages/Subscription/"));
const Files = lazyWithReload(() => import("../pages/Files/"));
const Prompts = lazyWithReload(() => import("../pages/Prompts"));
const QueueIntegration = lazyWithReload(() => import("../pages/QueueIntegration"));
const CampaignsPhrase = lazyWithReload(() => import("../pages/CampaignsPhrase"));
const FlowBuilder = lazyWithReload(() => import("../pages/FlowBuilder"));
const FlowBuilderConfig = lazyWithReload(() => import("../pages/FlowBuilderConfig"));

const featurePlanCache = new Map();

const getCachedFeaturePermission = (companyId, featureFlag) => {
  if (!companyId) {
    return null;
  }

  const cachedPlan = featurePlanCache.get(String(companyId));
  if (!cachedPlan || typeof cachedPlan[featureFlag] !== "boolean") {
    return null;
  }

  return cachedPlan[featureFlag];
};

const setCachedFeaturePermission = (companyId, featureFlag, allowed) => {
  if (!companyId) {
    return;
  }

  const cacheKey = String(companyId);
  const cachedPlan = featurePlanCache.get(cacheKey) || {};

  featurePlanCache.set(cacheKey, {
    ...cachedPlan,
    [featureFlag]: allowed
  });
};

const createFeatureGuardedPage = (
  WrappedComponent,
  { featureFlag, loaderMessage }
) => {
  const FeatureGuardedPage = props => {
    const { user } = useContext(AuthContext);
    const companyId = user?.companyId;
    const [featureState, setFeatureState] = useState(() => {
      const cachedPermission = getCachedFeaturePermission(companyId, featureFlag);

      if (cachedPermission === null) {
        return {
          status: "loading",
          allowed: false,
          companyId: companyId || null
        };
      }

      return {
        status: "ready",
        allowed: cachedPermission,
        companyId
      };
    });

    useEffect(() => {
      if (!companyId) {
        setFeatureState(current =>
          current.companyId === null && current.status === "loading"
            ? current
            : { status: "loading", allowed: false, companyId: null }
        );
        return undefined;
      }

      const cachedPermission = getCachedFeaturePermission(companyId, featureFlag);
      if (cachedPermission !== null) {
        setFeatureState(current =>
          current.companyId === companyId &&
            current.status === "ready" &&
            current.allowed === cachedPermission
            ? current
            : {
              status: "ready",
              allowed: cachedPermission,
              companyId
            }
        );
        return undefined;
      }

      let isMounted = true;

      setFeatureState(current =>
        current.companyId === companyId && current.status === "loading"
          ? current
          : { status: "loading", allowed: false, companyId }
      );

      api
        .get(`/companies/listPlan/${companyId}`)
        .then(({ data }) => {
          if (!isMounted) {
            return;
          }

          const allowed = Boolean(data?.plan?.[featureFlag]);
          setCachedFeaturePermission(companyId, featureFlag, allowed);
          setFeatureState({
            status: "ready",
            allowed,
            companyId
          });
        })
        .catch(err => {
          if (!isMounted) {
            return;
          }

          console.log(err);
          setFeatureState({
            status: "ready",
            allowed: false,
            companyId
          });
        });

      return () => {
        isMounted = false;
      };
    }, [companyId, featureFlag]);

    if (featureState.status !== "ready") {
      return <PageContentLoader message={loaderMessage} />;
    }

    if (!featureState.allowed) {
      return <Redirect to="/" />;
    }

    return <WrappedComponent {...props} />;
  };

  FeatureGuardedPage.displayName = `FeatureGuarded(${WrappedComponent.displayName || WrappedComponent.name || "Page"})`;

  return FeatureGuardedPage;
};

const campaignFeatureConfig = {
  featureFlag: "useCampaigns",
  loaderMessage: "Carregando..."
};

const automationFeatureConfig = {
  featureFlag: "useAutomation",
  loaderMessage: "Carregando..."
};

const openAiFeatureConfig = {
  featureFlag: "useOpenAi",
  loaderMessage: "Carregando..."
};

const ContactListsRoutePage = createFeatureGuardedPage(ContactLists, campaignFeatureConfig);
const ContactListItemsRoutePage = createFeatureGuardedPage(ContactListItems, campaignFeatureConfig);
const CampaignsRoutePage = createFeatureGuardedPage(Campaigns, campaignFeatureConfig);
const CampaignReportRoutePage = createFeatureGuardedPage(CampaignReport, campaignFeatureConfig);
const CampaignsConfigRoutePage = createFeatureGuardedPage(CampaignsConfig, campaignFeatureConfig);
const CampaignsPhraseRoutePage = createFeatureGuardedPage(CampaignsPhrase, automationFeatureConfig);
const FlowBuilderRoutePage = createFeatureGuardedPage(FlowBuilder, automationFeatureConfig);
const FlowBuilderConfigRoutePage = createFeatureGuardedPage(FlowBuilderConfig, automationFeatureConfig);
const QueueIntegrationRoutePage = createFeatureGuardedPage(QueueIntegration, automationFeatureConfig);
const managementPages = {
  "/connections": Connections,
  "/queues": Queues,
  "/users": Users,
  "/queue-integration": QueueIntegrationRoutePage,
};
const ManagementRoutePage = () => <Management pages={managementPages} />;
const PromptsRoutePage = createFeatureGuardedPage(Prompts, openAiFeatureConfig);

const AuthLoadingOverlay = () => {
  const { loading, loadingScreen, splashVisible } = useContext(AuthContext);

  if (!splashVisible && (!loading || loadingScreen !== "splash")) {
    return null;
  }

  return <BackdropLoading variant="splash" />;
};

const DeferredToastContainer = () => {
  const { loading, loadingScreen, splashVisible } = useContext(AuthContext);

  if (splashVisible || (loading && loadingScreen === "splash")) {
    return null;
  }

  return <ToastContainer autoClose={3000} />;
};

const Routes = () => {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ThemeProvider>
          <TicketsContextProvider>
            <AuthLoadingOverlay />
            <Switch>
              <Route exact path="/login" component={Login} />
              <Route exact path="/signup" component={Signup} />
              <Route exact path="/forgetpsw" component={ForgetPassword} />
              <Route exact path="/termos" component={TermsOfService} />
              <Route exact path="/privacidade" component={PrivacyPolicy} />
              <Route exact path="/exclusao-dados" component={DataDeletion} />
              <WhatsAppsProvider>
                <LoggedInLayout>
                  <Suspense fallback={<RouteTransitionFallback />}>
                    <Route exact path="/" component={Dashboard} isPrivate />
                    <Route
                      exact
                      path="/tickets/:ticketId?"
                      component={TicketResponsiveContainer}
                      isPrivate
                    />
                    <Route
                      exact
                      path={MANAGEMENT_PATHS}
                      component={ManagementRoutePage}
                      isPrivate
                    />
                    <Route
                      exact
                      path="/quick-messages"
                      component={QuickMessages}
                      isPrivate
                    />
                    <Route exact path="/todolist" component={ToDoList} isPrivate />
                    <Route
                      exact
                      path="/schedules"
                      component={Schedules}
                      isPrivate
                    />
                    <Route exact path="/tags" component={Tags} isPrivate />
                    <Route exact path="/contacts" component={Contacts} isPrivate />
                    <Route exact path="/helps" component={Helps} isPrivate />
                    <Route exact path="/files" component={Files} isPrivate />
                    <Route exact path="/prompts" component={PromptsRoutePage} isPrivate />
                    <Route
                      exact
                      path="/messages-api"
                      component={MessagesAPI}
                      isPrivate
                    />
                    <Route
                      exact
                      path="/settings"
                      component={SettingsCustom}
                      isPrivate
                    />
                    <Route exact path="/kanban" component={Kanban} isPrivate />
                    <Route
                      exact
                      path="/financeiro"
                      component={Financeiro}
                      isPrivate
                    />
                    <Route
                      exact
                      path="/announcements"
                      component={Annoucements}
                      isPrivate
                    />
                    <Route
                      exact
                      path="/subscription"
                      component={Subscription}
                      isPrivate
                    />
                    <Route exact path="/chats/:id?" component={Chat} isPrivate />
                    <Route
                      exact
                      path="/contact-lists"
                      component={ContactListsRoutePage}
                      isPrivate
                    />
                    <Route
                      exact
                      path="/contact-lists/:contactListId/contacts"
                      component={ContactListItemsRoutePage}
                      isPrivate
                    />
                    <Route
                      exact
                      path="/campaigns"
                      component={CampaignsRoutePage}
                      isPrivate
                    />
                    <Route
                      exact
                      path="/campaign/:campaignId/report"
                      component={CampaignReportRoutePage}
                      isPrivate
                    />
                    <Route
                      exact
                      path="/campaigns-config"
                      component={CampaignsConfigRoutePage}
                      isPrivate
                    />
                    <Route
                      exact
                      path="/phrase-lists"
                      component={CampaignsPhraseRoutePage}
                      isPrivate
                    />
                    <Route
                      exact
                      path="/flowbuilders"
                      component={FlowBuilderRoutePage}
                      isPrivate
                    />
                    <Route
                      exact
                      path="/flowbuilder/:id?"
                      component={FlowBuilderConfigRoutePage}
                      isPrivate
                    />
                  </Suspense>
                </LoggedInLayout>
              </WhatsAppsProvider>
            </Switch>
            <DeferredToastContainer />
          </TicketsContextProvider>
        </ThemeProvider>
      </AuthProvider>
    </BrowserRouter>
  );
};

export default Routes;
